import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import {
    MarkAsDirtyAction,
    MarkAsPristineAction,
    MarkAsUnsubmittedAction,
    MarkAsUntouchedAction,
    SetValueAction
} from 'ngrx-forms';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { of, TimeoutError } from 'rxjs/index';
import { catchError, concatMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BsModalAlertComponent, createAlertModalOptions } from '../../../../../core/modal/modal-alert.component';
import {
    BsModalConfirmComponent,
    ConfirmDialogResult,
    createConfirmRetryModalOptions
} from '../../../../../core/modal/modal-confirm.component';
import { AdminAccountService } from '../../../../../services/admin/admin-account.service';
import { ActionType, selectAction } from '../../../../../shared/state/index.state';
import { MediaDevice } from '../../models/user-device.model';
import { DevicesSharedNumberComponent } from '../component/shared-numbers/devices-shared-number.component';
import { UserDevicesActions } from './user-devices.actions';
import { FORM_ID } from './user-devices.reducer';
import { UserDevicesSelectors } from './user-devices.selectors';
import { UserSelectors } from '../../../../../user/state/user.selectors';

/**
 * https://www.intertech.com/Blog/ngrx-tutorial-actions-reducers-and-effects/
 * https://medium.com/frontend-fun/angular-ngrx-a-clean-and-clear-introduction-4ed61c89c1fc
 */

@Injectable()
export class AdminUserDevicesEffects {

    constructor(private actions$: Actions,
                private selectors: UserDevicesSelectors,
                private userSelectors: UserSelectors,
                private modalService: BsModalService,
                private adminAccountService: AdminAccountService) {
      // this.listenToRouter();
    }

    @Effect() getUserDevices$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.GET_USER_DEVICES),

            // TODO: Consider if we should emit a GET_USER_DEVICES_CANCEL if component is destroyed while request is in progress
            // (https://medium.com/simars/ngrx-http-effects-should-listen-for-cancel-action-227716178f70)

            withLatestFrom(this.userSelectors.selectSelectedUser()),

            switchMap(([_, user]) =>
                this.adminAccountService.getUserDevices(user.userId)
                    .pipe(
                        map((userDevices: Array<MediaDevice>) =>
                            UserDevicesActions.GET_USER_DEVICES_SUCCESS(userDevices)
                        ),

                        catchError(error =>
                            of(UserDevicesActions.GET_USER_DEVICES_FAIL({error, user}))
                        )
                    )
            )
        );

    @Effect() getDeviceFailed$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.GET_USER_DEVICES_FAIL),
            map(action => action.payload),
            switchMap((response: any) => {

                let reason = '';

                if (response !== undefined && response.error !== undefined &&
                    response.error.error !== undefined &&
                    response.error.error.error !== undefined &&
                    response.error.error.error.reason) {
                    reason = ` (${response.error.error.error.reason}).`;
                }

                const message = `Failed getting user's devices due to server error${reason}. Would you like to try again?`;

                const data: any = { user: response.user};
                const modalRef = this.modalService
                    .show(BsModalConfirmComponent, createConfirmRetryModalOptions('Temporary Server Error', message, data));

                return modalRef.content.onClose;
            }),
            map((dialogResult: ConfirmDialogResult) => { // map dialog result into action (map function returns observable to action)
                if (dialogResult.isConfirmed) {
                    return UserDevicesActions.GET_USER_DEVICES(dialogResult.data.user.userId);
                } else {
                    return {type: 'GET_USER_DEVICES_FAIL_HANDLED'};
                }
            })
        );

    /**
     * Side effect saving user devices from edited form (from the state store).
     *
     * NB: Using concatMap to ensure that the API request does not get cancelled by another save action
     * TODO: Let reducer handle state as well and toggle loading state
     */
    @Effect() saveUserDevices$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.SAVE_USER_DEVICES),
            withLatestFrom(this.selectors.selectFormStateValue()),
            withLatestFrom(this.userSelectors.selectSelectedUser()),
            map(([[action, formValue], user]) => {

                const devices = [
                    formValue.smsDevice,
                    formValue.emailDevice,
                    formValue.voiceDevice
                ];

//                const userId = user.userId; // For some reason user.userId is not accepted

                return {devices, user};
            }),

            concatMap(latest => // Use concatMap to ensure the API request completes and not cancelled by more save actions
                this.adminAccountService.addUpdateUserDevice(latest.devices, latest.user.userId)
                        .pipe(
                            switchMap(devices => {

                                // TODO: Should we use something like this?
                                //       Coz if we uncheck sms device, userSenderAddress will be automatically reset to empty string in DB
                                // if((devices as any).devices[0].userSenderAddress.length == 0)
                                //   add to return array new SetValueAction(`${FORM_ID}.smsDevice.userSenderAddress`, '')

                                return [
                                    new SetValueAction(`${FORM_ID}.smsDevice.hasAutoResponse`, true), // TODO: Remove
                                    UserDevicesActions.SAVE_USER_DEVICE_SUCCESS(devices),
                                    new MarkAsPristineAction(FORM_ID),
                                    // new MarkAsUntouchedAction(FORM_ID),
                                    new MarkAsUnsubmittedAction(FORM_ID)
                                ];
                            }),

                            // We place catchError handlers inside a pipe of the API call
                            // This way, the new (handled) Observable only replaces the failed one
                            // inside the switch, rather than the whole Effect Observable.
                            // https://medium.com/city-pantry/handling-errors-in-ngrx-effects-a95d918490d9
                            catchError(error => {
                                let errorObject = error;

                                if (error.error !== undefined) {
                                    errorObject = error.error;

                                    // if (error.error.error !== undefined) {
                                    //    errorObject = error.error.error;
                                    // }
                                }

                                return of(UserDevicesActions.SAVE_USER_DEVICE_FAIL(errorObject));
                            })
                        )
            )
        );

    @Effect() saveDeviceFailed$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.SAVE_USER_DEVICE_FAIL),
            map(action => (action as ActionType<any>).payload),
            switchMap(error => { // switch the pipe to observe closing the dialog

                let message = 'Saving devices failed due to some server error ';
                let title = 'Saving devices error';

                const alreadyUsedBy = error.numberAlreadyUsedBy;
                if (alreadyUsedBy !== undefined && alreadyUsedBy !== '') {
                    message = `The number entered for SMS Device is already mapped with ${alreadyUsedBy}. ` +
                        'Please enter another number.';
                    title = 'Saving rejected';
                } else {
                    if (error instanceof TimeoutError) {
                        console.log(error);
                        message += 'connection to server timed out';
                    } else {
                        let errorObject = error.error;
                        if (errorObject === undefined) {
                            console.log(error);
                            message += 'undefined error'; // This might be due to: ResponseException.reason: Request cancelled due to request timeout (120sec) reached (skipped waitForDone)
                        } else {
                            if (errorObject.error !== undefined) {
                                errorObject = errorObject.error;
                            }

                            if (errorObject.reason !== undefined) {
                                message += `(${errorObject.reason})`;
                            }

                        }
                    }
                }

                const modalRef = this.modalService
                    .show(BsModalAlertComponent, createAlertModalOptions(title, message));

                return modalRef.content.onClose;
            }),
            // Emit actions (to revert form state) only after alert dialog is closed to make it more clear that you can try again
            switchMap(_ => [
                new MarkAsUnsubmittedAction(FORM_ID),
                new MarkAsUntouchedAction(FORM_ID) // Not sure this should be done
            ])
        );

    @Effect() openSharedNumbersDialog$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.OPEN_SHARED_NUMBERS_DIALOG),

            switchMap(_ => { // switch the pipe to observe selecting number in the dialog (on closing dialog)
                const modalRef = this.modalService
                    .show(DevicesSharedNumberComponent, DevicesSharedNumberComponent.modalOptions);

                return modalRef.content.selectedNumber$.asObservable();
            }),
            filter(selectedNumber => selectedNumber !== undefined),
            switchMap((selectedNumber: any) => [
                // Update specific field in the device
                new SetValueAction(`${FORM_ID}.smsDevice.userSenderAddress`, selectedNumber),
                new SetValueAction(`${FORM_ID}.smsDevice.isSharedNumber`, true),
                new MarkAsDirtyAction(FORM_ID)
            ])
        );

    @Effect() makeUserDevicesFormPristine$ = this.actions$
        .pipe(
            selectAction(UserDevicesActions.MAKE_USER_DEVICES_FORM_PRISTINE),
            switchMap(_ => [
                new MarkAsPristineAction(FORM_ID)
            ])
        );

    // Currently route change/ tab click event is handled by angular1
    //    @Effect() routeChange$ = this.actions$.pipe(
//      selectAction(UserDevicesActions.ALERT_ROUTE_CHANGE_WITHOUT_SAVING),
//      switchMap(_ => { // switch the pipe to observe selecting number in the dialog (on closing dialog)
//        const message = 'You have unsaved changes that will be lost. Are you sure you want to leave this page?';
//        const message = 'You have unsaved changes that will be lost. Are you sure you want to leave this page?';
//        const modalRef = this.modalService
//          .show(BsModalConfirmComponent, createConfirmRetryModalOptions('Temporary Server Error', message));
//          .show(BsModalConfirmComponent, createConfirmRetryModalOptions('Temporary Server Error', message));
//        return modalRef.content.onClose;
//      }),
//      map((dialogResult: ConfirmDialogResult)  => { // map dialog result into action (map function returns observable to action)
//        if (dialogResult.isConfirmed) {
//          return UserDevicesActions.RESET_DIRTY_FORM;
//        } else {
//          return {type: 'ROUTE_CANCEL'};
//        }
//      })
//  );
//  );
//    private listenToRouter(): void {
//    this.router.events
//      .pipe(
//        filter(event => event instanceof NavigationStart),
//        filter(event => event.url.indexOf('devices') < 0),
//        withLatestFrom(this.selectors.selectIsFormDirty())
//      )
//      .subscribe(([event, isFormDirty]) => {
//        // Check if navigating from devices without saving changes
//        if (isFormDirty) {
//          (new UserDevicesActions(this.store).alertRouteChangeWithoutSaving(0));
//        }
//      });
//  }

    @Effect() unmarkNumberAsShared$ = this.actions$
    .pipe(
        selectAction(UserDevicesActions.UN_MARK_AS_SHARED_NUMBER),
        switchMap(_ => [
            // Update specific field in the device
            new SetValueAction(`${FORM_ID}.smsDevice.isSharedNumber`, false),
            new MarkAsDirtyAction(FORM_ID)
        ])
    );
}
